import {
  Button,
  Checkbox,
  Divider,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { useSearchConfig } from "../../context/SearchConfigContext";
import { FilteringOperator } from "../../models/person.model";

export function SearchConfigModal() {
  const { config, updateConfig, resetConfig, isModalOpen, closeModal } = useSearchConfig();

  // Create local state to track changes before saving
  const [localConfig, setLocalConfig] = useState(config);

  // Update local state when modal opens or config changes
  useEffect(() => {
    if (isModalOpen) {
      setLocalConfig(config);
    }
  }, [isModalOpen, config]);

  // Save changes and close modal
  const handleSave = () => {
    updateConfig(localConfig);
    closeModal();
  };

  // Reset to defaults
  const handleReset = () => {
    const defaultConfig = {
      matchTechThreshold: 0.85, // 85%
      matchCertThreshold: 0.85, // 85%
      maxTechMatches: 5,
      maxCertMatches: 5,
      exactMatchOnly: false,
      exactTechMatchOnly: false,
      exactCertMatchOnly: false,
      useTechSearch: true,
      useCertSearch: true,
      filteringOperator: 0, // FilteringOperator.Or
      debounceDelay: 1200,
    };
    setLocalConfig(defaultConfig);
    resetConfig();
  };

  // Update local state for a specific field
  const updateLocalConfig = <K extends keyof typeof localConfig>(
    key: K,
    value: typeof localConfig[K]
  ) => {
    setLocalConfig({
      ...localConfig,
      [key]: value,
    });
  };

  return (
    <Modal
      opened={isModalOpen}
      onClose={closeModal}
      title={<Title order={4}>Search Configuration</Title>}
      size="lg"
    >
      <Stack>
        <Text size="sm" c="dimmed">
          Configure how search works in the CV list. These settings will be saved for future sessions.
        </Text>

        <Divider label="Search Timing" labelPosition="center" />

        <Tooltip label="How long to wait after you stop typing before starting the search">
          <NumberInput
            label="Typing Delay (seconds)"
            description="How long to wait after you stop typing before searching starts. Higher values wait longer."
            value={localConfig.debounceDelay / 1000} // Convert from ms to seconds for display
            onChange={(val) => updateLocalConfig("debounceDelay", (Number(val) || 1.2) * 1000)} // Convert back to ms for storage
            min={0}
            max={5}
            step={0.1}
            decimalScale={1}
          />
        </Tooltip>

        <Divider label="Search Operator" labelPosition="center" />

        <Tooltip label="How to combine multiple search keywords">
          <Select
            label="Filtering Operator"
            description="How to combine multiple search terms"
            value={localConfig.filteringOperator.toString()}
            onChange={(value) => {
              if (value !== null) {
                updateLocalConfig(
                  "filteringOperator",
                  parseInt(value) as FilteringOperator
                );
              }
            }}
            data={[
              {
                value: FilteringOperator.Or.toString(),
                label: "OR (Any keyword)",
              },
              {
                value: FilteringOperator.And.toString(),
                label: "AND (All keywords)",
              },
            ]}
          />
        </Tooltip>

        <Divider label="Exact Matching" labelPosition="center" />

        <Text size="xs" c="dimmed">
          "Match Exactly", "Use Technologies", and "Use Certificates" are also available as quick toggles in the search filter bar.
        </Text>

        <Group>
          <Tooltip label="Only exact matches for technology searches">
            <Checkbox
              label="Exact Tech Match Only"
              checked={localConfig.exactTechMatchOnly}
              onChange={(e) => updateLocalConfig("exactTechMatchOnly", e.currentTarget.checked)}
            />
          </Tooltip>
        </Group>

        <Group>
          <Tooltip label="Only exact matches for certificate searches">
            <Checkbox
              label="Exact Certificate Match Only"
              checked={localConfig.exactCertMatchOnly}
              onChange={(e) => updateLocalConfig("exactCertMatchOnly", e.currentTarget.checked)}
            />
          </Tooltip>
        </Group>

        <Group>
          <Tooltip label="Enable keyword matching against technology dictionary">
            <Checkbox
              label="Use Technology Dictionary"
              checked={localConfig.useTechSearch}
              onChange={(e) => updateLocalConfig("useTechSearch", e.currentTarget.checked)}
            />
          </Tooltip>
        </Group>

        <Group>
          <Tooltip label="Enable keyword matching against certificate dictionary">
            <Checkbox
              label="Use Certificate Dictionary"
              checked={localConfig.useCertSearch}
              onChange={(e) => updateLocalConfig("useCertSearch", e.currentTarget.checked)}
            />
          </Tooltip>
        </Group>

        <Divider label="Fuzzy Matching" labelPosition="center" />

        <Group grow>
          <Tooltip label="Technology matching threshold (0% - 100%)">
            <NumberInput
              label="Technology Threshold (%)"
              description="Higher values require closer matches"
              value={localConfig.matchTechThreshold * 100} // Convert from 0-1 to 0-100 for display
              onChange={(val) => updateLocalConfig("matchTechThreshold", (Number(val) || 85) / 100)} // Convert back to 0-1 for storage
              min={0}
              max={100}
              step={5}
              suffix="%"
            />
          </Tooltip>

          <Tooltip label="Certificate matching threshold (0% - 100%)">
            <NumberInput
              label="Certificate Threshold (%)"
              description="Higher values require closer matches"
              value={localConfig.matchCertThreshold * 100} // Convert from 0-1 to 0-100 for display
              onChange={(val) => updateLocalConfig("matchCertThreshold", (Number(val) || 85) / 100)} // Convert back to 0-1 for storage
              min={0}
              max={100}
              step={5}
              suffix="%"
            />
          </Tooltip>
        </Group>

        <Group grow>
          <Tooltip label="Maximum technology matches per keyword">
            <NumberInput
              label="Max Tech Matches"
              description="Limit results per keyword"
              value={localConfig.maxTechMatches}
              onChange={(val) => updateLocalConfig("maxTechMatches", Number(val) || 5)}
              min={1}
              max={20}
            />
          </Tooltip>

          <Tooltip label="Maximum certificate matches per keyword">
            <NumberInput
              label="Max Certificate Matches"
              description="Limit results per keyword"
              value={localConfig.maxCertMatches}
              onChange={(val) => updateLocalConfig("maxCertMatches", Number(val) || 5)}
              min={1}
              max={20}
            />
          </Tooltip>
        </Group>

        <Group justify="space-between" mt="md">
          <Button variant="outline" color="red" onClick={handleReset}>
            Reset to Defaults
          </Button>
          <Group>
            <Button variant="outline" onClick={closeModal}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save Changes</Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
